import inRange from 'lodash/inRange';
import isEqual from 'lodash/isEqual';
import last from 'lodash/last';

import {Section, SectionItem} from 'common/components/items-menu/types';
import {FlatSortConfig, SortBy, SortConfig, SortOrder} from 'common/report/util/sorting/types';
import {PropertyType, ReportName} from 'common/report/constants';

import {
  CellRenderContext,
  ColSortOptionValue,
  CommonDataTableFields,
  DataColumn,
  RowType,
  SegmentColumn,
  TableColumn,
  TableColumnType,
  TableDataType,
  TableRow,
  TrackName,
} from './types';

export function getNumSegments(columns: TableColumn[]) {
  return columns.filter(isSegmentColumn).length;
}

export function invertSortOrder(sortOrder: SortOrder) {
  return sortOrder === SortOrder.Descending ? SortOrder.Ascending : SortOrder.Descending;
}

export function getSortSelectSections({
  columns,
  column,
  sortConfig,
}: {
  columns: TableColumn[];
  column: SegmentColumn;
  sortConfig: SortConfig;
}): Array<Section<ColSortOptionValue>> {
  const labelSortAsc = {sortBy: SortBy.Label, sortOrder: SortOrder.Ascending};
  const labelSortDesc = {sortBy: SortBy.Label, sortOrder: SortOrder.Descending};
  const segmentIndex = parseInt(column.field.slice(CommonDataTableFields.SegmentPrefix.length));
  const colSortConfig = sortConfig.colSortAttrs[segmentIndex];
  const sortingByColumns = sortConfig.sortBy === SortBy.Column;

  const valueItems = getSortSelectValueItems({
    columns,
    colSortConfig,
    segmentIndex,
    sortingByColumns,
  });

  let labelSortTextParts: [string, string];
  if (column.contentType === PropertyType.Number) {
    labelSortTextParts = [`1`, `100`];
  } else if (column.contentType === PropertyType.Datetime) {
    labelSortTextParts = [`Jan`, `Dec`];
  } else {
    labelSortTextParts = [`A`, `Z`];
  }

  return [
    {
      items: [
        {
          icon: `sort-alpha-asc`,
          label: `Segment ${labelSortTextParts[0]} → ${labelSortTextParts[1]}`,
          value: {
            segmentIndex,
            sort: labelSortAsc,
          },
          isSelected: sortingByColumns && isEqual(labelSortAsc, colSortConfig),
        },
        {
          icon: `sort-alpha-desc`,
          label: `Segment ${labelSortTextParts[1]} → ${labelSortTextParts[0]}`,
          value: {
            segmentIndex,
            sort: labelSortDesc,
          } as ColSortOptionValue,
          isSelected: sortingByColumns && isEqual(labelSortDesc, colSortConfig),
        },
        ...valueItems,
      ],
    },
  ];
}

function getColSortOptionValue({
  segmentIndex,
  sortOrder,
  valueField,
}: {
  segmentIndex: number;
  sortOrder: SortOrder;
  valueField: string;
}): ColSortOptionValue {
  return {
    segmentIndex,
    sort: {sortBy: SortBy.Value, sortOrder, valueField},
  };
}

function getValueSortSelectSections({
  colSortConfig,
  segmentIndex,
  sortOrder,
  parentSelected,
  columns,
}: {
  colSortConfig: FlatSortConfig;
  segmentIndex: number;
  sortOrder: SortOrder;
  parentSelected: boolean;
  columns: TableColumn[];
}): Section[] {
  const sectionItems = columns.reduce((accum, col) => {
    if (col.sortSelectTitle && isDataColumn(col)) {
      const icon = {
        [TableDataType.Text]: `type-text`,
        [TableDataType.Number]: `type-number`,
      }[col.contentType];
      const value = getColSortOptionValue({segmentIndex, sortOrder, valueField: col.field});
      accum.push({
        icon,
        label: col.sortSelectTitle,
        value,
        isSelected: parentSelected && colSortConfig.sortBy === SortBy.Value && colSortConfig?.valueField === col.field,
      });
    }
    return accum;
  }, []);
  return [{items: sectionItems}];
}

export function getSortSelectValueItems({
  colSortConfig,
  segmentIndex,
  sortingByColumns,
  columns,
}: {
  colSortConfig: FlatSortConfig;
  segmentIndex: number;
  sortingByColumns: boolean;
  columns: TableColumn[];
}): SectionItem[] {
  let valueItems: SectionItem[];

  const groupSortableCols = columns.filter((col) => col.sortSelectTitle);
  const singleSortableCol = groupSortableCols.length === 1 ? groupSortableCols[0] : null;

  let valueSelected = false;
  if (sortingByColumns && colSortConfig?.sortBy === SortBy.Value) {
    if (singleSortableCol) {
      // the menu item might not be selected if sorting on a "hidden" column, like cohortSize
      valueSelected = colSortConfig?.valueField === singleSortableCol.field;
    } else {
      valueSelected = true;
    }
  }

  const ascValueSelected = valueSelected && colSortConfig?.sortOrder === SortOrder.Ascending;
  const descValueSelected = valueSelected && colSortConfig?.sortOrder === SortOrder.Descending;

  if (singleSortableCol) {
    const valueField = singleSortableCol.field;
    const label = singleSortableCol.sortSelectTitle;

    valueItems = [
      {
        icon: `sort-value-asc`,
        label: `${label} 1 → 100`,
        isSelected: ascValueSelected,
        value: getColSortOptionValue({
          segmentIndex,
          sortOrder: SortOrder.Ascending,
          valueField,
        }),
      },
      {
        icon: `sort-value-desc`,
        label: `${label} 100 → 1`,
        isSelected: descValueSelected,
        value: getColSortOptionValue({
          segmentIndex,
          sortOrder: SortOrder.Descending,
          valueField,
        }),
      },
    ];
  } else {
    const ascValueSubsections = getValueSortSelectSections({
      colSortConfig,
      segmentIndex,
      sortOrder: SortOrder.Ascending,
      parentSelected: ascValueSelected,
      columns,
    });
    const descValueSubsections = getValueSortSelectSections({
      colSortConfig,
      segmentIndex,
      sortOrder: SortOrder.Descending,
      parentSelected: descValueSelected,
      columns,
    });

    valueItems = [
      {
        icon: `sort-value-asc`,
        label: `Value 1 → 100`,
        subSections: ascValueSubsections,
        isSelected: ascValueSelected,
      },
      {
        icon: `sort-value-desc`,
        label: `Value 100 → 1`,
        subSections: descValueSubsections,
        isSelected: descValueSelected,
      },
    ];
  }
  return valueItems;
}

export function isSegmentColumn(column: TableColumn): column is SegmentColumn {
  return column.type === TableColumnType.Segment;
}

export function isDataColumn(column: TableColumn): column is DataColumn {
  return column.type === TableColumnType.Data;
}

export function isTextStyleColumn(column: TableColumn) {
  return (
    column.type === TableColumnType.Segment ||
    (column.type === TableColumnType.Data && column.contentType === TableDataType.Text)
  );
}

export function isNumberStyleColumn(column: TableColumn) {
  return column.type === TableColumnType.Data && column.contentType === TableDataType.Number;
}

export function isSameRowByContext(contextA: CellRenderContext, contextB: CellRenderContext) {
  if (!contextA || !contextB) {
    return false;
  } else if (contextA.rowType === RowType.GroupHeader && contextB.rowType === RowType.GroupHeader) {
    return true;
  } else if (contextA.rowType === RowType.Header && contextB.rowType === RowType.Header) {
    return true;
  } else if (contextA.rowType === RowType.Body && contextB.rowType === RowType.Body) {
    return Boolean(contextA.rowIdx === contextB.rowIdx && contextA.groupedRowIdx === contextB.groupedRowIdx);
  } else {
    return false;
  }
}

export function isSameCellByContext(contextA: CellRenderContext, contextB: CellRenderContext) {
  return isSameRowByContext(contextA, contextB) && contextA.columnIdx === contextB.columnIdx;
}

export function getSummedColumnWidth(columns: TableColumn[]) {
  const lastColumn = last(columns);
  return columns.length ? lastColumn.offsetLeft + lastColumn.computedWidth - columns[0].offsetLeft : 0;
}

export function getSummedRowHeight(rows: TableRow[]): number {
  const lastRow = last(rows);
  return rows.length ? lastRow.offsetTop + lastRow.rowHeight - rows[0].offsetTop : 0;
}

export function getReportNameFromTrackName(trackName: TrackName): ReportName {
  return {
    [TrackName.InsightsLine]: ReportName.Insights,
    [TrackName.FunnelsSteps]: ReportName.Funnels,
    [TrackName.FunnelsTrends]: ReportName.Funnels,
    [TrackName.RetentionCurve]: ReportName.Retention,
    [TrackName.RetentionTrends]: ReportName.Retention,
  }[trackName];
}

export function areIntersectingRanges(
  [range1StartIdx, range1EndIdx]: [number, number],
  [range2StartIdx, range2EndIdx]: [number, number],
): boolean {
  const range1InclusiveEndIdx = range1EndIdx - 1;
  const range2InclusiveEndIdx = range2EndIdx - 1;
  return (
    inRange(range2StartIdx, range1StartIdx, range1EndIdx) ||
    inRange(range2InclusiveEndIdx, range1StartIdx, range1EndIdx) ||
    inRange(range1StartIdx, range2StartIdx, range2EndIdx) ||
    inRange(range1InclusiveEndIdx, range2StartIdx, range2EndIdx)
  );
}
